//july30 CreateFormWrapper
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Button } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFormContext } from '../contexts/FormContext';
import { checkUserPermission } from '../utils/firestore';
import { useLoading } from '../contexts/LoadingContext';

// Component that handles the logic of creating a new form
const CreateFormComponent = ({ isCreatingForm, onFormCreated }) => {
  const { currentUser } = useAuth();
  const {
    createNewFormInFirestore, // Not used but destructured for parity
    initializeNewFormLocally,
    setFormId,
    formId
  } = useFormContext();
  const { setLoading } = useLoading();
  const isCreatingRef = useRef(false);

  const handleCreateNewForm = useCallback(async () => {
    console.log('[CreateFormComponent] handleCreateNewForm triggered');

    if (isCreatingRef.current) {
      console.warn('[CreateFormComponent] Form creation is already in progress.');
      return;
    }

    try {
      const userId = currentUser?.uid;
      if (!userId) {
        console.warn('[CreateFormComponent] User not authenticated');
        alert('User not authenticated');
        return;
      }

      isCreatingRef.current = true;

      setLoading(true, 'formCreation');

      const hasPermission = await checkUserPermission(userId, 'createForms');
      console.log(`[CreateFormComponent] User permission to create forms: ${hasPermission}`);

      if (hasPermission) {
        const newFormId = initializeNewFormLocally(userId);

        if (newFormId) {
          console.log('[CreateFormComponent] New form initialized locally with ID:', newFormId);
          setFormId(newFormId);
          localStorage.setItem('formId', newFormId);
          onFormCreated(newFormId);
        } else {
          console.error('[CreateFormComponent] Failed to create new form ID.');
          alert('Failed to create new form.');
        }
      } else {
        alert('You do not have permission to create new forms.');
      }
    } catch (error) {
      console.error('[CreateFormComponent] Error checking permissions or creating new form:', error);
      alert('Failed to verify permissions or create a new form.');
    } finally {
      setLoading(false, 'formCreation');
      isCreatingRef.current = false;
    }
}, [currentUser, initializeNewFormLocally,
    setLoading, setFormId, onFormCreated]);

  // Trigger the effect when flagged
  useEffect(() => {
    if (isCreatingForm && currentUser && !isCreatingRef.current) {
      console.log('[CreateFormComponent] Starting form creation via effect.');
      handleCreateNewForm();
    }
  }, [handleCreateNewForm, currentUser, isCreatingForm]);

  return null; // No UI here; handled by wrapper button
};

// Wrapper for your zero-state New Form button
const CreateFormWrapper = () => {
  const [isCreatingForm, setIsCreatingForm] = useState(false);
  const { currentUser } = useAuth();
  const { setLoading } = useLoading();
  const navigate = useNavigate();

const handleFormCreated = useCallback((newFormId) => {
  console.log(
    '[CreateFormWrapper] Form created, navigating to builder with ID:',
    newFormId,
  );

  navigate(`/form-builder/${newFormId}/design`, {
    state: { createNewForm: true },
  });

  // Re-arm the wrapper so the next click works
  setIsCreatingForm(false);
}, [navigate]);

  const handleCreateNewForm = useCallback(async () => {
    console.log('[CreateFormWrapper] New Form button clicked');

    const userId = currentUser?.uid;
    if (!userId) {
      console.warn('[CreateFormWrapper] User not authenticated');
      alert('User not authenticated');
      return;
    }

    setLoading(true, 'permissionCheck');

    const hasPermission = await checkUserPermission(userId, 'createForms');
    console.log(`[CreateFormWrapper] Permission check result: ${hasPermission}`);

    if (hasPermission) {
      setIsCreatingForm(true); // Will mount CreateFormComponent to generate and navigate
    } else {
      alert('You do not have permission to create new forms.');
    }

    setLoading(false, 'permissionCheck');
  }, [currentUser, setLoading]);

  if (!currentUser) return null;

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddCircleOutlineIcon />}
        onClick={handleCreateNewForm}
        sx={{ marginTop: '16px' }}
      >
        New Form
      </Button>

      {isCreatingForm && (
        <CreateFormComponent
          isCreatingForm={isCreatingForm}
          onFormCreated={handleFormCreated}
        />
      )}
    </>
  );
};

export default CreateFormWrapper;
